<template>
    <a-modal
        :visible="true"
        width="60vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="ctm-wrap"
        @cancel="$emit('close')"
    >
        <div class="ctm-modal">
            <div class="ctm-head">
                <div>
                    <h3>Categorias de produtos</h3>
                    <div class="ctm-sub">Gerencie as categorias do seu catálogo</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="ctm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="ctm-form">
                <div class="ctm-form-title">Nova categoria</div>
                <a-row :gutter="[12, 12]">
                    <a-col :xs="24" :md="7">
                        <a-input v-model="newCategory.cat_codInterno" placeholder="Código interno" @pressEnter="create" />
                    </a-col>
                    <a-col :xs="24" :md="12">
                        <a-input v-model="newCategory.cat_descricao" placeholder="Nome da categoria" @pressEnter="create" />
                    </a-col>
                    <a-col :xs="24" :md="5">
                        <a-button type="primary" block :loading="creating" @click="create">
                            <a-icon type="plus" /> Adicionar
                        </a-button>
                    </a-col>
                </a-row>
                <div v-if="formError" class="ctm-error">{{ formError }}</div>
            </div>

            <div class="ctm-tools">
                <a-input v-model="search" class="ctm-search" placeholder="Buscar categoria" allow-clear>
                    <a-icon slot="prefix" type="search" />
                </a-input>
            </div>

            <div class="ctm-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="filteredCategories"
                    row-key="cat_id"
                    :loading="loading"
                    :pagination="{ pageSize: 30, size: 'small' }"
                    :locale="{ emptyText: 'Nenhuma categoria cadastrada.' }"
                >
                    <template slot="id" slot-scope="text">#{{ text }}</template>
                    <template slot="nome" slot-scope="text, cat">
                        <a-input
                            v-if="editingId === cat.cat_id"
                            v-model="editingName"
                            size="small"
                            @pressEnter="saveName(cat)"
                        />
                        <span v-else>{{ text }}</span>
                    </template>
                    <template slot="acoes" slot-scope="text, cat">
                        <ActionMenu :actions="getActions(cat)" />
                    </template>
                </a-table>
            </div>

            <div class="ctm-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import CategoryService from "@/services/v1/category.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";

export default {
    name: "CategoriesV2Modal",
    mixins: [loaderMixin],
    components: { ActionMenu },
    data() {
        return {
            service: new CategoryService(),
            loading: false,
            creating: false,
            categories: [],
            search: "",
            newCategory: {},
            formError: "",
            editingId: null,
            editingName: "",
            columns: [
                { title: "ID", dataIndex: "cat_id", key: "cat_id", width: 90, scopedSlots: { customRender: "id" } },
                { title: "Código interno", dataIndex: "cat_codInterno", key: "cat_codInterno", width: 160 },
                { title: "Nome", dataIndex: "cat_descricao", key: "cat_descricao", scopedSlots: { customRender: "nome" } },
                { title: "Ações", key: "acoes", align: "center", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    computed: {
        filteredCategories() {
            const term = (this.search || "").trim().toLowerCase();
            if (!term) return this.categories;
            return this.categories.filter(
                (c) => String(c.cat_descricao || "").toLowerCase().includes(term) || String(c.cat_codInterno || "").toLowerCase().includes(term)
            );
        },
    },
    created() {
        this.load();
    },
    methods: {
        handleError(error) {
            const message = error.response && error.response.data && error.response.data.mensagem;
            if (message) this.$swal.fire("Desculpe.", message, "error");
            else ErrorHandlerService.handle(error, this.$store);
        },
        load() {
            this.loading = true;
            return this.service
                .all()
                .then(({ data }) => {
                    this.categories = data.categorias || [];
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        create() {
            if (!this.newCategory.cat_codInterno || !this.newCategory.cat_descricao) {
                this.formError = "Informe o código interno e o nome da categoria.";
                return;
            }
            this.formError = "";
            this.creating = true;
            this.service
                .create(this.newCategory)
                .then(() => {
                    this.newCategory = {};
                    this.$emit("updated");
                    return this.load();
                })
                .catch(this.handleError)
                .finally(() => {
                    this.creating = false;
                });
        },
        getActions(cat) {
            const editing = this.editingId === cat.cat_id;
            return [
                editing
                    ? { label: "Salvar nome", icon: "save", iconClass: "action-success", onClick: () => this.saveName(cat) }
                    : { label: "Renomear categoria", icon: "edit", iconClass: "action-info", onClick: () => this.startEditing(cat) },
                { divider: true },
                { label: "Excluir categoria", icon: "delete", iconClass: "action-danger", onClick: () => this.remove(cat) },
            ];
        },
        startEditing(cat) {
            this.editingId = cat.cat_id;
            this.editingName = cat.cat_descricao;
        },
        saveName(cat) {
            const name = (this.editingName || "").trim();
            if (!name) return;
            this.loading = true;
            this.service
                .update({ ...cat, cat_descricao: name })
                .then(() => {
                    this.editingId = null;
                    this.$emit("updated");
                    return this.load();
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleError(error);
                });
        },
        remove(cat) {
            this.confirm_action({
                message: "Confirma remoção da categoria?",
                subtitle: `Categoria ${cat.cat_descricao} será removida!`,
                callback: () => {
                    this.loading = true;
                    this.service
                        .delete(cat.cat_id)
                        .then(() => {
                            this.$emit("updated");
                            return this.load();
                        })
                        .catch((error) => {
                            this.loading = false;
                            this.handleError(error);
                        });
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.ctm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.ctm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.ctm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.ctm-x.ant-btn,
.ctm-x.ant-btn:focus,
.ctm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ctm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.ctm-form {
    margin: 0 20px 12px;
    padding: 12px 14px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
}
.ctm-form-title {
    font-weight: 600;
    font-size: 13px;
    color: #262626;
    margin-bottom: 8px;
}
.ctm-error {
    margin-top: 6px;
    font-size: 12px;
    color: #cf1322;
}
.ctm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.ctm-search {
    flex: 1 1 240px;
    max-width: 360px;
}
.ctm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.ctm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.ctm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
